/**
 * MAIN HUB HOME PAGE - AUTHORITATIVE DESIGN
 * drdnreddyortho.com - Primary Authority Site
 * Upper Limb & Sports Injury Specialist
 * 
 * Design Principles:
 * - Academic, authoritative, calm tone
 * - Credentials visible within first 2 scrolls
 * - Evidence-based positioning
 * - No marketing hype
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useSite } from '../contexts/SiteContext';
import { SEO, SEO_CONFIG } from '../components/SEO';
import { PressStrip } from '../components/PressStrip';
import { 
  ArrowRight, 
  Award, 
  Clock, 
  Users, 
  Star,
  ExternalLink,
  CheckCircle2,
  Phone,
  Calendar,
  MapPin,
  GraduationCap,
  Building2,
  BadgeCheck,
  Quote,
  Sparkles,
  Shield,
  Globe,
  BookOpen,
  FileText,
  Stethoscope
} from 'lucide-react';

export const MainHubHome = () => {
  const { 
    theme,
    doctor,
    specialties,
    symptoms
  } = useSite();

  const [isVisible, setIsVisible] = useState(false);

  // Safe fallbacks
  const safeTheme = theme || { primaryHex: '#0e7490', lightBgHex: '#f0f9ff' };
  const safeDoctor = doctor || {
    name: 'Dr. Deepthi Nandan Reddy',
    title: 'Upper Limb & Sports Injury Specialist',
    credentials: 'MBBS, MS Ortho, FRCS (UK), CCT (UK)',
    fellowships: 'UK & USA Fellowship Trained',
    consultationLink: '#',
    phone: '+91 99599 64567',
    doctorImage: '',
    hospital: 'Apollo Hospitals',
    hospitalFull: 'Apollo Hospitals, Financial District',
    timings: 'Mon-Sat: 9AM-5PM',
    reviewCount: '143',
    whatsappLink: '#',
    fellowshipDetails: [],
    academicHighlights: [],
    leadershipRoles: [],
    memberships: []
  };
  const safeSpecialties = specialties || [];
  const safeSymptoms = symptoms || [];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { number: '30+', label: 'Years Experience', icon: Clock },
    { number: '15000+', label: 'Patients Treated', icon: Users },
    { number: '5000+', label: 'Surgeries', icon: Award },
    { number: '5.0', label: 'Google Rating', icon: Star }
  ];

  // Key credentials for authority positioning
  const keyCredentials = [
    { label: 'UK CCT', description: 'Certificate of Completion of Training (UK)' },
    { label: 'FRCS', description: 'Fellow, Royal College of Surgeons (Edinburgh & UK)' },
    { label: 'Mayo Fellow', description: 'British Shoulder & Elbow Society - Mayo Clinic Travelling Fellowship' },
    { label: 'SESI President', description: 'President, Shoulder & Elbow Society of India' }
  ];

  // Why patients are referred here
  const whyReferred = [
    'Complex revision surgeries after failed primary procedures',
    'Sports injuries requiring arthroscopic intervention',
    'Shoulder instability requiring Latarjet or bone block procedures',
    'Multi-ligament elbow reconstructions',
    'Cases requiring international standard of care',
    'Second opinions for complex upper limb conditions'
  ];

  return (
    <div className="min-h-screen" data-testid="main-hub-home">
      <SEO {...SEO_CONFIG.home} />
      
      {/* Hero Section - Premium Glossy Design */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Simplified Background for better LCP - reduced animations */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950">
          <div className="absolute inset-0 grid-pattern opacity-30" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-500/20 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-600/10 rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content - Credentials First */}
            <div className={`text-white ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
              {/* Premium Badge with Glow */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass animate-border-glow mb-6">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-300 font-medium text-sm tracking-wide">
                  WORLD-CLASS UPPER LIMB SPECIALIST
                </span>
              </div>
              
              {/* Name with Gradient */}
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight">
                <span className="gradient-text-white">{safeDoctor.name}</span>
              </h1>
              
              {/* Title - Specific, Not Generic */}
              <p className="text-xl md:text-2xl font-light mb-2 text-cyan-300">
                {safeDoctor.title}
              </p>
              <p className="text-lg text-gray-400 mb-4 italic">
                "Combining precision surgery with personalised rehabilitation"
              </p>

              {/* Floating Credential Pills with Glass Effect */}
              <div className="flex flex-wrap gap-3 mb-6">
                {['Fellowship Trained (UK & USA)', 'International Expert', 'World-Class Care', 'Patient-Centered'].map((item, i) => (
                  <div key={i} className={`glass-card px-4 py-2 rounded-full text-sm text-gray-200 animate-fade-in-up stagger-${i+1}`}>
                    <CheckCircle2 className="w-4 h-4 inline mr-2 text-cyan-400" />
                    {item}
                  </div>
                ))}
              </div>

              {/* Key Credentials - Glassmorphism Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {keyCredentials.map((cred, index) => (
                  <div 
                    key={index}
                    className={`glass rounded-lg p-3 hover-lift animate-fade-in-up stagger-${index+1}`}
                  >
                    <p className="text-cyan-400 font-bold text-sm">{cred.label}</p>
                    <p className="text-gray-400 text-xs leading-tight">{cred.description}</p>
                  </div>
                ))}
              </div>

              {/* Credentials Box - Premium Glass Effect */}
              <div className="glass rounded-2xl p-6 mb-6 hover-glow transition-all duration-300">
                <div className="flex items-start gap-3 mb-4">
                  <GraduationCap className="w-6 h-6 text-amber-400 mt-0.5 animate-float" />
                  <div>
                    <p className="text-amber-400 font-semibold text-xs uppercase tracking-wider mb-1">Qualifications</p>
                    <p className="text-white text-sm">{safeDoctor.credentials}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-cyan-400 mt-0.5 animate-float" style={{ animationDelay: '0.5s' }} />
                  <div>
                    <p className="text-cyan-400 font-semibold text-xs uppercase tracking-wider mb-1">Fellowship Training</p>
                    <p className="text-gray-300 text-sm">{safeDoctor.fellowships}</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons - Premium Style with proper touch targets */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={safeDoctor.consultationLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block min-h-[48px]"
                >
                  <Button 
                    size="lg" 
                    className="btn-premium btn-glow bg-cyan-700 hover:bg-cyan-800 text-white px-8 py-4 rounded-xl w-full sm:w-auto text-lg min-h-[48px]"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Appointment
                  </Button>
                </a>
                <a 
                  href={`tel:${safeDoctor.phone}`}
                  className="inline-block min-h-[48px]"
                >
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="glass border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-xl w-full sm:w-auto text-lg min-h-[48px]"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    {safeDoctor.phone}
                  </Button>
                </a>
              </div>

              {/* Authority page link (SEO hero → orthopedic-surgeon-hyderabad) */}
              <div className="mt-4">
                <Link to="/orthopedic-surgeon-hyderabad" className="inline-flex items-center gap-1.5 text-cyan-200 hover:text-white underline underline-offset-4 text-sm font-medium" data-testid="hero-ortho-hyderabad-link">
                  Looking for the best orthopedic surgeon in Hyderabad? →
                </Link>
              </div>

              {/* Rating Badge */}
              <div className="flex items-center gap-4 mt-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-400">5.0 Google Rating</span>
                <span className="text-cyan-400 font-semibold">15000+ Patients</span>
              </div>
            </div>

            {/* Right - Doctor Profile with Premium Effects */}
            <div className={`relative ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`}>
              {/* Decorative Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-teal-500/30 rounded-3xl blur-2xl transform scale-95" />
              
              {/* Floating credential badges - Glass Effect */}
              <div className="absolute -top-4 -left-4 z-20 glass rounded-lg p-3 animate-float">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">SESI President</p>
                    <p className="text-gray-400 text-xs">National Leadership</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 z-20 glass rounded-lg p-3 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-green-400 fill-green-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">5.0 Rating</p>
                    <p className="text-gray-400 text-xs">{safeDoctor.reviewCount} Reviews</p>
                  </div>
                </div>
              </div>

              {/* Main image with Premium Border - OPTIMIZED for LCP */}
              <div className="relative rounded-3xl overflow-hidden image-shine image-glow border-2 border-white/10">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src="/hero/doctor-543.avif"
                    srcSet="/hero/doctor-400.avif 400w, /hero/doctor-543.avif 543w"
                    sizes="(max-width: 1024px) 90vw, 500px"
                    alt={`${safeDoctor.name} - Upper Limb Surgeon at ${safeDoctor.hospital}`}
                    className="w-full h-full object-cover object-top"
                    width="543"
                    height="724"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                  />
                </div>
                
                {/* Location overlay - Glassmorphism */}
                <div className="absolute bottom-4 left-4 right-4 glass rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-white font-semibold text-sm">{safeDoctor.hospitalFull}</p>
                      <p className="text-gray-400 text-xs">{safeDoctor.timings}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar - Premium Floating Card */}
      <section className="relative -mt-12 z-20 px-4">
        <div className="container mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8 card-hover">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className={`text-center animate-fade-in-up stagger-${index+1}`}>
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-4xl font-bold gradient-text">{stat.number}</p>
                  <p className="text-gray-500 text-sm font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PressStrip />

      {/* Why Patients Are Referred Section - Premium Design */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white section-pattern">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 font-medium text-sm mb-4">
                <Shield className="w-4 h-4" />
                Expert Referral Center
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Patients Are Referred Here
              </h2>
              <p className="text-lg text-gray-600">
                Complex cases requiring subspecialty expertise and international standard of care
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {whyReferred.map((reason, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-3 bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover-lift animate-fade-in-up stagger-${(index % 6) + 1}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600" />
                  </div>
                  <p className="text-gray-700">{reason}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-gradient-to-r from-cyan-50 to-teal-50 rounded-2xl border border-cyan-100 hover-glow">
              <p className="text-cyan-800 text-center">
                <strong>Note:</strong> Dr. Reddy is one of few Indian surgeons with UK NHS Consultant experience 
                and Certificate of Completion of Training (CCT), ensuring international standards of care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subspecialty Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Subspecialty Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fellowship-trained expertise across all upper limb conditions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {specialties?.map((specialty, index) => (
              <a 
                key={specialty.id} 
                href={specialty.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="h-full border-2 border-gray-100 hover:border-cyan-200 transition-colors">
                  <CardHeader>
                    <div 
                      className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center"
                      style={{ backgroundColor: `${specialty.color}15` }}
                    >
                      <Stethoscope className="w-7 h-7" style={{ color: specialty.color }} />
                    </div>
                    <CardTitle className="text-xl">{specialty.title}</CardTitle>
                    <p className="text-sm text-gray-500">{specialty.subtitle}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4">{specialty.description}</p>
                    <div className="flex items-center gap-2 text-cyan-700 group-hover:text-cyan-800 font-semibold">
                      <span className="text-sm font-semibold">Learn More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Fellowship Training & Experience */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              International Fellowship Training
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Trained at world-renowned centres in the UK and USA
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {safeDoctor.fellowshipDetails?.map((fellowship, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-5 h-5 text-cyan-400" />
                  <span className="text-gray-400 text-sm">{fellowship.location}</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-1">{fellowship.institution}</h3>
                <p className="text-gray-400 text-sm">{fellowship.type}</p>
                {fellowship.year && (
                  <p className="text-cyan-400 text-sm mt-2">{fellowship.year}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic & Research */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Academic Contributions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Published researcher and international conference faculty, combining clinical excellence 
                with scholarly output for evidence-based patient care.
              </p>

              <div className="space-y-4">
                {safeDoctor.academicHighlights?.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Professional Leadership</h3>
              <div className="space-y-4">
                {safeDoctor.leadershipRoles?.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                    <BadgeCheck className="w-5 h-5 text-cyan-600" />
                    <p className="text-gray-800 font-medium">
                      {typeof item === 'string' ? item : `${item.role}, ${item.organization}${item.period ? ` (${item.period})` : ''}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Memberships */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Memberships
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {safeDoctor.memberships?.map((membership, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-100"
              >
                <Shield className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                <p className="text-gray-700 text-sm">{membership}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When to Seek Specialist Care */}
      {symptoms && symptoms.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  When to See an Upper Limb Specialist
                </h2>
                <p className="text-lg text-gray-600">
                  Early specialist consultation can prevent progression and improve outcomes
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {symptoms.map((symptom, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-4 rounded-lg bg-cyan-50 border-l-4 border-cyan-600"
                  >
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{symptom}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Medical Disclaimer - Fixed contrast */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-700 text-sm">
              <strong className="text-gray-800">Medical Disclaimer:</strong> The information provided on this website is for educational purposes only 
              and is not intended to replace professional medical advice, diagnosis, or treatment. Individual results may vary. 
              Please consult with Dr. Reddy or your healthcare provider for personalized medical advice.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Schedule a Consultation
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              For complex upper limb conditions requiring specialist care, 
              book a consultation to discuss your treatment options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={safeDoctor.consultationLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="bg-cyan-700 hover:bg-cyan-800 text-white px-10 py-6 rounded-xl"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                </Button>
              </a>
              <a href={safeDoctor.whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-xl"
                >
                  WhatsApp Enquiry
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
